import type { ColorByRule } from 'components/color-by-attribute-modal';
import { formatNumber } from 'utils/number';
import { AREA_EXPORT_ATTRIBUTES } from 'pages/area-geometries/list/catalog-utils';
import type { DatasetConfig, FilterFieldConfig } from '../types';

/**
 * A list filter over one facet: a searchable checkbox list with Select All.
 * @param {string} id Filter id.
 * @param {string} label Label shown in the filter modal, and its category.
 * @param {string} param Request param the selected ids are sent on.
 * @param {string} facetKey Response facet the options come from.
 * @return {FilterFieldConfig} The filter field config.
 */
const listFilter = (
  id: string,
  label: string,
  param: string,
  facetKey: string
): FilterFieldConfig => ({
  id,
  label,
  category: label,
  type: 'multi-select-list',
  param,
  facetKey,
});

/**
 * The area's page, at the same URL the Areas page links to: by business id, so it
 * always opens the current version. The row's `href` names this one version.
 * @param {Record<string, unknown>} row The table row.
 * @return {string | null} The page's path, or null without a business id.
 */
export const areaPageHref = (row: Record<string, unknown>): string | null => {
  const { businessId } = row;
  return typeof businessId === 'string' || typeof businessId === 'number'
    ? `/area_geometries/${businessId}`
    : null;
};

/** The dataset's colour (tab dot, swatch and default fill): #0E20A7 at 20%, from UAT. */
export const AREAS_LAYER_COLOR = 'rgba(14, 32, 167, 0.2)';

// Areas start coloured by Area Type (UAT), each type in its own colour and opacity.
// The values are the stored categories the facets and tiles carry; any other type
// keeps the layer colour.
const AREA_TYPE_COLORS: Record<string, string> = {
  AOI: 'rgba(16, 231, 145, 0.4)',
  Fault: 'rgba(255, 179, 0, 0.4)',
  Regulatory: 'rgba(0, 118, 244, 0.5)',
  TypeCurve: 'rgba(248, 0, 0, 0.25)',
  Unit: 'rgba(0, 21, 178, 0.2)',
};
export const DEFAULT_AREA_TYPE_RULE: ColorByRule = {
  ruleId: 'default-areas-category',
  attribute: 'category',
  selectedValues: Object.keys(AREA_TYPE_COLORS),
  selectedValueLabels: Object.keys(AREA_TYPE_COLORS),
  valueColors: AREA_TYPE_COLORS,
  applyTo: { map: true, table: true },
  enabled: true,
  groupId: 'areas',
};

/**
 * Gross acres as the mock prints them: grouped, with at most one decimal and none
 * when the value is whole ("1,294.7", "367,340").
 * @param {unknown} value The row's gross acres.
 * @return {string} The formatted acreage.
 */
export const formatAcres = (value: unknown): string =>
  typeof value === 'number' ? formatNumber(value, 0, 1) : '';

/**
 * Areas (INS-13107): the Area Builder catalog as a Map Query dataset. Every area
 * is listed, filtered and drawn; creating one is Story 2.
 *
 * The backend keeps its field names (`category`, `data_source`) on rows, facets and
 * tiles alike, and this config labels them. Rows carry `href` and a `boundingBox`; the
 * facets' ids are the values the tiles carry, so a filter or a colour rule matches
 * table and map alike.
 */
export const areasConfig: DatasetConfig = {
  id: 'areas',
  usesGlobalText: true,
  label: 'Areas',
  searchEndpoint: '/queries/area_geometries',
  // Joins the Map Query spatial-export roster as the backend 'area_geometries' dataset, which reuses
  // GisExport::Sources::AreaGeometry's layer so Map Query exports the same area polygons as the
  // standalone Areas export. Attribute ids match GisExport::Sources::AreaGeometry::AREA_COLUMNS.
  spatialExport: {
    key: 'area_geometries',
    attributes: AREA_EXPORT_ATTRIBUTES,
  },
  directHref: '/queries/area_geometries',
  tileEndpoint: '/queries/area_geometries/mvt',
  defaultLayerColor: AREAS_LAYER_COLOR,
  // "Show on Map" fits the map to the area's corners; null until it is indexed.
  extentKey: 'boundingBox',
  highlightRowKey: 'id',
  highlightFeatureKey: 'id',
  // The selected area keeps its own colour, stronger and haloed, as amber reads
  // like Portfolio Assets; and Show on map clears it again (UAT).
  highlightInOwnColor: true,
  showOnMapToggles: true,
  // Checked areas combine into a new one (Story 2).
  areaSelection: {
    kind: 'areas',
    singular: 'Area',
    geometry: 'polygon',
    idKey: 'id',
    nameKey: 'name',
  },
  defaultPayload: {
    text: '',
    page: 1,
    pageSize: 100,
    // Search matches every column, not just the name (the backend's default, kept
    // narrow for the Areas catalog's typeahead).
    search_scope: 'all',
    // Only shared areas until the reader changes it (UAT); a chip they can remove.
    shared: ['true'],
  },

  // Fill and outline from one source, so each tile is fetched once. Largest first,
  // so a small area stays visible over a big one it sits inside. The attribute is
  // `boundary`, not `geometry`, so its layer ids cannot collide with Documents'.
  tileLayers: [
    {
      geoAttribute: 'boundary',
      layerType: 'fill',
      minZoom: 0,
      fillOpacity: 0.25,
      outline: { opacity: 0.9 },
      sortKey: ['*', -1, ['coalesce', ['get', 'gross_acres'], 0]],
    },
  ],

  defaultFilterCategory: 'Area Type',
  // The mock's filter modal names them so; the table's column reads "Status".
  filters: [
    listFilter('category', 'Area Type', 'category', 'category'),
    listFilter('status', 'Area Status', 'status', 'status'),
    listFilter('shared', 'Shared', 'shared', 'shared'),
    listFilter('data_source', 'Source', 'data_source', 'dataSource'),
  ],

  colorByAttributes: [
    {
      facetKey: 'category',
      label: 'Area Type',
      mvtProperty: 'category',
      rowField: 'category',
    },
    {
      facetKey: 'status',
      label: 'Status',
      mvtProperty: 'status',
      rowField: 'status',
    },
    // Tiles carry `shared` as a boolean; the rule's values are the facet's "true" / "false".
    {
      facetKey: 'shared',
      label: 'Shared',
      mvtProperty: 'shared',
      matchAsString: true,
    },
    {
      facetKey: 'dataSource',
      label: 'Source',
      mvtProperty: 'data_source',
      rowField: 'dataSource',
    },
  ],

  // Column ids double as the backend's sort whitelist. Order, headers and widths are
  // the mock's, measured; the table stretches them to fill, so their ratios are
  // what carries over.
  columns: [
    {
      id: 'name',
      header: 'Area Name',
      accessorKey: 'name',
      type: 'link',
      rowHref: areaPageHref,
      size: 344,
      sortable: true,
    },
    {
      id: 'category',
      header: 'Area Type',
      accessorKey: 'category',
      size: 123,
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 98,
      sortable: true,
      // No area carries a status until the backend sources one.
      formatValue: (value: unknown) =>
        typeof value === 'string' && value ? value : '—',
    },
    {
      id: 'shared',
      header: 'Shared',
      accessorKey: 'shared',
      size: 103,
      sortable: true,
      formatValue: (value: unknown) => (value ? 'Shared' : 'Private'),
    },
    {
      id: 'data_source',
      header: 'Source',
      accessorKey: 'dataSource',
      size: 154,
      sortable: true,
    },
    {
      id: 'gross_acres',
      header: 'Gross Acres',
      accessorKey: 'grossAcres',
      size: 135,
      sortable: true,
      align: 'left',
      formatValue: formatAcres,
    },
    {
      id: 'created_by',
      header: 'Created By',
      accessorKey: 'createdBy',
      size: 164,
      sortable: true,
    },
  ],
};
